import AssetManagement from "@/services/api/assetManagement";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {
    assetGroups: boolean;
    assetItems: boolean;
    assetLocations: boolean;
    assetTypes: boolean;
    groupIds: boolean;
    typeIds: boolean;
    locationIds: boolean;
    locationTypes: boolean;
    assetItemIds: boolean;
    allocationGroups: boolean;
    allocation: boolean;
    allocationExport: boolean;
    assetItemExport: boolean;
    assetItemReport: boolean;
    assetItemLogs: boolean;
    exportFile: boolean;
    exportTypeFile: boolean;
    exportGroupFile: boolean;
    exportGroup: boolean;
    exportType: boolean;
    exportLocation: boolean;
    allocationLogs: boolean;
}

interface SaleOrderState {
    assetGroupList: any[];
    assetItemList: any[];
    assetLocationList: any[];
    assetTypeList: any[];
    groupIdsList: any[];
    typeIdsList: any[];
    locationIdsList: any[];
    locationTypeList: any[];
    assetItemIdsList: any[];
    allocationGroupList: any[];
    allocationItemList: any[];
    itemReportList: any[];
    itemLogList: any[];
    allocationLogList: {
        TotalRecords: number;
        Items: any[];
    };
    loading: LoadingState;
    error: string | null;
}

const initialState: SaleOrderState = {
    assetGroupList: [],
    assetItemList: [],
    assetLocationList: [],
    assetTypeList: [],
    groupIdsList: [],
    typeIdsList: [],
    locationIdsList: [],
    locationTypeList: [],
    assetItemIdsList: [],
    allocationGroupList: [],
    allocationItemList: [],
    itemReportList: [],
    itemLogList: [],
    allocationLogList: {
        TotalRecords: 0,
        Items: [],
    },
    loading: {
        assetGroups: false,
        assetItems: false,
        assetTypes: false,
        assetLocations: false,
        groupIds: false,
        typeIds: false,
        locationIds: false,
        locationTypes: false,
        assetItemIds: false,
        allocationGroups: false,
        allocation: false,
        allocationExport: false,
        assetItemExport: false,
        assetItemReport: false,
        assetItemLogs: false,
        exportFile: false,
        exportTypeFile: false,
        exportGroupFile: false,
        exportGroup: false,
        exportType: false,
        exportLocation: false,
        allocationLogs: false,
    },
    error: null,
};

export const getAssetAllGroups = createAsyncThunk(
    "asset/getAssetAllGroups",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetAllGroups(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset groups",
            );
        }
    },
);

export const addAssetGroup = createAsyncThunk(
    "asset/addAssetGroup",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.addAssetGroup(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add asset group",
            );
        }
    },
);

export const deleteGroupById = createAsyncThunk(
    "asset/deleteGroupById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.deleteGroupById(id);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to delete asset group",
            );
        }
    },
);

export const getAssetGroupIds = createAsyncThunk(
    "asset/getAssetGroupIds",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetGroupIds();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset groups ids",
            );
        }
    },
);

export const getAssetAllItems = createAsyncThunk(
    "asset/getAssetAllItems",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetAllItems(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset items",
            );
        }
    },
);

export const addAssetItem = createAsyncThunk(
    "asset/addAssetItem",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.addAssetItem(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add asset item",
            );
        }
    },
);

export const addAssetItemWithFiles = createAsyncThunk(
    "asset/addAssetItemWithFiles",
    async (
        {
            payload,
            files,
            removeFileIds,
        }: { payload: any; files: File[]; removeFileIds: any[] },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const res = await dispatch(addAssetItem(payload)).unwrap();

            const newFiles = files.filter((file: any) => !file?.isExisting);

            if ((newFiles.length > 0 || removeFileIds.length > 0) && res) {
                const formData = new FormData();

                if (newFiles.length) {
                    newFiles.forEach((file: File) => {
                        formData.append("mediaFile", file);
                    });
                }

                if (removeFileIds.length) {
                    formData.append(
                        "removeFiles",
                        JSON.stringify({ RemoveIds: removeFileIds })
                    );
                }

                await AssetManagement.addAssetItemsByIdFiles({
                    id: res.Id,
                    logId: res.LogId,
                    type: "Add",
                    formData,
                });
            }

            return res;
        } catch (err: any) {
            return rejectWithValue(
                err ||
                err?.response?.data?.message ||
                "Failed to add asset item with files",
            );
        }
    },
);

export const updateAllocationItemWithFiles = createAsyncThunk(
    "asset/updateAllocationWithFiles",
    async (
        {
            data,
            files,
            action,
        }: { data: any; files: File[]; action: "allocate" | "return" },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const res = await dispatch(updateAllocationItem(data)).unwrap();

            if (files?.length > 0 && data?.AssetItemId) {
                const formData = new FormData();

                files.forEach((file: File) => {
                    formData.append("mediaFile", file);
                });

                await dispatch(
                    addAssetItemsByIdFiles({
                        id: res.Id,
                        logId: res.LogId,
                        type: action === "allocate" ? "Allocate" : "Return",
                        formData
                    })
                ).unwrap();
            }

            return res;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to allocate items");
        };
    }
);

export const deleteItemById = createAsyncThunk(
    "asset/deleteItemById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.deleteItemById(id);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to delete asset item",
            );
        }
    },
);

export const getAssetItemsById = createAsyncThunk(
    "asset/getAssetItemsById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemsById(id);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset items by id",
            );
        }
    },
);

export const getAssetItemsVendor = createAsyncThunk(
    "asset/getAssetItemsVendor",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemsVendor();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset items vendor",
            );
        }
    },
);

export const updateAssetItemsBulkUpload = createAsyncThunk(
    "asset/updateAssetItemsBulkUpload",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.updateAssetItemsBulkUpload(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to update asset items bulk upload",
            );
        }
    },
);

export const getAssetItemsBulkUploadFiles = createAsyncThunk(
    "asset/getAssetItemsBulkUploadFiles",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemsBulkUploadFiles(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset item bulk upload files",
            );
        }
    },
);

export const getAssetItemsBulkUploadReport = createAsyncThunk(
    "asset/getAssetItemsBulkUploadReport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemsBulkUploadReport(data);
            return saveAsExcelFile(response, "ITEM_REPORT");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset item bulk upload report",
            );
        }
    },
);

export const getAssetItemsBulkUploadLogs = createAsyncThunk(
    "asset/getAssetItemsBulkUploadLogs",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemsBulkUploadLogs(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset item bulk upload logs",
            );
        }
    },
);

export const getAssetItemsByIdFiles = createAsyncThunk(
    "asset/getAssetItemsByIdFiles",
    async ({ id, data }: { id: number, data: { type: string } }, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemsByIdFiles(id, data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset item files",
            );
        }
    },
);

export const addAssetItemsByIdFiles = createAsyncThunk(
    "asset/addAssetItemsByIdFiles",
    async (data: { id: number, logId: number, type: string, formData: any }, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.addAssetItemsByIdFiles(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add asset items by id files",
            );
        }
    },
);

export const getAssetAllTypes = createAsyncThunk(
    "asset/getAssetAllTypes",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetAllTypes(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset types",
            );
        }
    },
);

export const addAssetType = createAsyncThunk(
    "asset/addAssetType",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.addAssetType(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add asset type",
            );
        }
    },
);

export const deleteTypeById = createAsyncThunk(
    "asset/deleteTypeById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.deleteTypeById(id);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to delete asset type",
            );
        }
    },
);

export const getAssetAllLocations = createAsyncThunk(
    "asset/getAssetAllLocations",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetAllLocations(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch asset locations",
            );
        }
    },
);

export const addAssetLocation = createAsyncThunk(
    "asset/addAssetLocation",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.addAssetLocation(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to add asset location",
            );
        }
    },
);

export const deleteLocationById = createAsyncThunk(
    "asset/deleteLocationById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.deleteLocationById(id);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to delete asset location",
            );
        }
    },
);

export const getLocationNames = createAsyncThunk(
    "asset/getLocationNames",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getLocationNames();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to get location names",
            );
        }
    },
);

export const getLocationTypes = createAsyncThunk(
    "asset/getLocationTypes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getLocationTypes();
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to get location types",
            );
        }
    },
);

export const getTypeNames = createAsyncThunk(
    "asset/getTypeNames",
    async (data: { groupId: number }, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getTypeNames(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to get type names",
            );
        }
    },
);

export const getAssetItemNames = createAsyncThunk(
    "asset/getAssetItemNames",
    async (data: { typeId: number }, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemNames(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to get item names",
            );
        }
    },
);

export const getAllocationGroups = createAsyncThunk(
    "asset/getAllocationGroups",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAllocationGroups(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to get allocation groups",
            );
        }
    },
);

export const getAllocationItem = createAsyncThunk(
    "asset/getAllocationItem",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAllocationItem(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch allocation item",
            );
        }
    },
);

export const updateAllocationItem = createAsyncThunk(
    "asset/updateAllocationItem",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.updateAllocationItem(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to update item status",
            );
        }
    },
);

export const getAllocationExport = createAsyncThunk(
    "asset/getAllocationExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAllocationExport(data);
            return saveAsExcelFile(response, "Allocation");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getAssetItemExport = createAsyncThunk(
    "asset/getAssetItemExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAssetItemExport(data);
            return saveAsExcelFile(response, "Asset_Item");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getReportDownloadFile = createAsyncThunk(
    "asset/getReportDownloadFile",
    async (fileId: number, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getReportDownloadFile(fileId);
            return saveAsExcelFile(response, "ASSET_REPORT");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getAllocationTypeExport = createAsyncThunk(
    "asset/getAllocationTypeExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAllocationTypeExport(data);
            return saveAsExcelFile(response, "ASSET_TYPE");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getAllocationGroupExport = createAsyncThunk(
    "asset/getAllocationGroupExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAllocationGroupExport(data);
            return saveAsExcelFile(response, "ALLOCATION_GROUPS");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getGroupExport = createAsyncThunk(
    "asset/getGroupExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getGroupExport(data);
            return saveAsExcelFile(response, "ASSET_GROUPS");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getTypeExport = createAsyncThunk(
    "asset/getTypeExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getTypeExport(data);
            return saveAsExcelFile(response, "ASSET_TYPES");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getLocationExport = createAsyncThunk(
    "asset/getLocationExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getLocationExport(data);
            return saveAsExcelFile(response, "ASSET_LOCATIONS");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

export const getAllocationLogs = createAsyncThunk(
    "asset/getAllocationLogs",
    async (data: { assetId: number, from: number, to: number }, { rejectWithValue }) => {
        try {
            const response = await AssetManagement.getAllocationLogs(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch logs",
            );
        }
    },
);

const assetManagementSlice = createSlice({
    name: "asset",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAssetAllGroups.pending, (state) => {
                state.loading.assetGroups = true;
                state.error = null;
                state.assetGroupList = [];
            })
            .addCase(getAssetAllGroups.fulfilled, (state, action) => {
                state.loading.assetGroups = false;
                state.assetGroupList = action.payload || [];
            })
            .addCase(getAssetAllGroups.rejected, (state, action) => {
                state.loading.assetGroups = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetAllItems.pending, (state) => {
                state.loading.assetItems = true;
                state.error = null;
                state.assetItemList = [];
            })
            .addCase(getAssetAllItems.fulfilled, (state, action) => {
                state.loading.assetItems = false;
                state.assetItemList = action.payload || [];
            })
            .addCase(getAssetAllItems.rejected, (state, action) => {
                state.loading.assetItems = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetAllLocations.pending, (state) => {
                state.loading.assetLocations = true;
                state.error = null;
                state.assetLocationList = [];
            })
            .addCase(getAssetAllLocations.fulfilled, (state, action) => {
                state.loading.assetLocations = false;
                state.assetLocationList = action.payload || [];
            })
            .addCase(getAssetAllLocations.rejected, (state, action) => {
                state.loading.assetLocations = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetAllTypes.pending, (state) => {
                state.loading.assetTypes = true;
                state.error = null;
                state.assetTypeList = [];
            })
            .addCase(getAssetAllTypes.fulfilled, (state, action) => {
                state.loading.assetTypes = false;
                state.assetTypeList = action.payload || [];
            })
            .addCase(getAssetAllTypes.rejected, (state, action) => {
                state.loading.assetTypes = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetGroupIds.pending, (state) => {
                state.loading.groupIds = true;
                state.error = null;
                state.groupIdsList = [];
            })
            .addCase(getAssetGroupIds.fulfilled, (state, action) => {
                state.loading.groupIds = false;
                state.groupIdsList = action.payload || [];
            })
            .addCase(getAssetGroupIds.rejected, (state, action) => {
                state.loading.groupIds = false;
                state.error = action.payload as string;
            })

            .addCase(getTypeNames.pending, (state) => {
                state.loading.typeIds = true;
                state.error = null;
                state.typeIdsList = [];
            })
            .addCase(getTypeNames.fulfilled, (state, action) => {
                state.loading.typeIds = false;
                state.typeIdsList = action.payload || [];
            })
            .addCase(getTypeNames.rejected, (state, action) => {
                state.loading.typeIds = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetItemNames.pending, (state) => {
                state.loading.assetItemIds = true;
                state.error = null;
                state.assetItemIdsList = [];
            })
            .addCase(getAssetItemNames.fulfilled, (state, action) => {
                state.loading.assetItemIds = false;
                state.assetItemIdsList = action.payload || [];
            })
            .addCase(getAssetItemNames.rejected, (state, action) => {
                state.loading.assetItemIds = false;
                state.error = action.payload as string;
            })

            .addCase(getLocationNames.pending, (state) => {
                state.loading.locationIds = true;
                state.error = null;
                state.locationIdsList = [];
            })
            .addCase(getLocationNames.fulfilled, (state, action) => {
                state.loading.locationIds = false;
                state.locationIdsList = action.payload || [];
            })
            .addCase(getLocationNames.rejected, (state, action) => {
                state.loading.locationIds = false;
                state.error = action.payload as string;
            })

            .addCase(getLocationTypes.pending, (state) => {
                state.loading.locationTypes = true;
                state.error = null;
                state.locationTypeList = [];
            })
            .addCase(getLocationTypes.fulfilled, (state, action) => {
                state.loading.locationTypes = false;
                state.locationTypeList = action.payload || [];
            })
            .addCase(getLocationTypes.rejected, (state, action) => {
                state.loading.locationTypes = false;
                state.error = action.payload as string;
            })

            .addCase(getAllocationGroups.pending, (state) => {
                state.loading.allocationGroups = true;
                state.error = null;
                state.allocationGroupList = [];
            })
            .addCase(getAllocationGroups.fulfilled, (state, action) => {
                state.loading.allocationGroups = false;
                state.allocationGroupList = action.payload || [];
            })
            .addCase(getAllocationGroups.rejected, (state, action) => {
                state.loading.allocationGroups = false;
                state.error = action.payload as string;
            })

            .addCase(getAllocationItem.pending, (state) => {
                state.loading.allocation = true;
                state.error = null;
                state.allocationItemList = [];
            })
            .addCase(getAllocationItem.fulfilled, (state, action) => {
                state.loading.allocation = false;
                state.allocationItemList = action.payload || [];
            })
            .addCase(getAllocationItem.rejected, (state, action) => {
                state.loading.allocation = false;
                state.error = action.payload as string;
            })

            .addCase(getAllocationExport.pending, (state) => {
                state.loading.allocationExport = true;
                state.error = null;
            })
            .addCase(getAllocationExport.fulfilled, (state) => {
                state.loading.allocationExport = false;
            })
            .addCase(getAllocationExport.rejected, (state, action) => {
                state.loading.allocationExport = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetItemExport.pending, (state) => {
                state.loading.assetItemExport = true;
                state.error = null;
            })
            .addCase(getAssetItemExport.fulfilled, (state) => {
                state.loading.assetItemExport = false;
            })
            .addCase(getAssetItemExport.rejected, (state, action) => {
                state.loading.assetItemExport = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetItemsBulkUploadFiles.pending, (state) => {
                state.loading.assetItemReport = true;
                state.itemReportList = [];
                state.error = null;
            })
            .addCase(getAssetItemsBulkUploadFiles.fulfilled, (state, action) => {
                state.loading.assetItemReport = false;
                state.itemReportList = action.payload || [];
            })
            .addCase(getAssetItemsBulkUploadFiles.rejected, (state, action) => {
                state.loading.assetItemReport = false;
                state.error = action.payload as string;
            })

            .addCase(getAssetItemsBulkUploadLogs.pending, (state) => {
                state.loading.assetItemLogs = true;
                state.itemLogList = [];
                state.error = null;
            })
            .addCase(getAssetItemsBulkUploadLogs.fulfilled, (state, action) => {
                state.loading.assetItemLogs = false;
                state.itemLogList = action.payload || [];
            })
            .addCase(getAssetItemsBulkUploadLogs.rejected, (state, action) => {
                state.loading.assetItemLogs = false;
                state.error = action.payload as string;
            })

            .addCase(getReportDownloadFile.pending, (state) => {
                state.loading.exportFile = true;
                state.error = null;
            })
            .addCase(getReportDownloadFile.fulfilled, (state) => {
                state.loading.exportFile = false;
            })
            .addCase(getReportDownloadFile.rejected, (state, action) => {
                state.loading.exportFile = false;
                state.error = action.payload as string;
            })

            .addCase(getAllocationTypeExport.pending, (state) => {
                state.loading.exportTypeFile = true;
                state.error = null;
            })
            .addCase(getAllocationTypeExport.fulfilled, (state) => {
                state.loading.exportTypeFile = false;
            })
            .addCase(getAllocationTypeExport.rejected, (state, action) => {
                state.loading.exportTypeFile = false;
                state.error = action.payload as string;
            })

            .addCase(getAllocationGroupExport.pending, (state) => {
                state.loading.exportGroupFile = true;
                state.error = null;
            })
            .addCase(getAllocationGroupExport.fulfilled, (state) => {
                state.loading.exportGroupFile = false;
            })
            .addCase(getAllocationGroupExport.rejected, (state, action) => {
                state.loading.exportGroupFile = false;
                state.error = action.payload as string;
            })

            .addCase(getGroupExport.pending, (state) => {
                state.loading.exportGroup = true;
                state.error = null;
            })
            .addCase(getGroupExport.fulfilled, (state) => {
                state.loading.exportGroup = false;
            })
            .addCase(getGroupExport.rejected, (state, action) => {
                state.loading.exportGroup = false;
                state.error = action.payload as string;
            })

            .addCase(getTypeExport.pending, (state) => {
                state.loading.exportType = true;
                state.error = null;
            })
            .addCase(getTypeExport.fulfilled, (state) => {
                state.loading.exportType = false;
            })
            .addCase(getTypeExport.rejected, (state, action) => {
                state.loading.exportType = false;
                state.error = action.payload as string;
            })

            .addCase(getLocationExport.pending, (state) => {
                state.loading.exportLocation = true;
                state.error = null;
            })
            .addCase(getLocationExport.fulfilled, (state) => {
                state.loading.exportLocation = false;
            })
            .addCase(getLocationExport.rejected, (state, action) => {
                state.loading.exportLocation = false;
                state.error = action.payload as string;
            })
            .addCase(getAllocationLogs.pending, (state, action) => {
                state.loading.allocationLogs = true;
                state.error = null;

                // only clear on first page
                if (action.meta.arg.from === 1) {
                    state.allocationLogList = {
                        TotalRecords: 0,
                        Items: [],
                    };
                }
            })
            .addCase(getAllocationLogs.fulfilled, (state, action) => {
                state.loading.allocationLogs = false;
                const newItems = action.payload?.Items || [];

                if (action.meta.arg.from === 1) {
                    state.allocationLogList = action.payload;
                } else {
                    // append unique items
                    const existingItems = state.allocationLogList?.Items || [];

                    const mergedItems = [
                        ...existingItems,
                        ...newItems.filter(
                            (newItem: any) =>
                                !existingItems.some(
                                    (existing: any) => existing.Id === newItem.Id
                                )
                        ),
                    ];

                    state.allocationLogList = {
                        ...action.payload,
                        Items: mergedItems,
                    };
                }

                state.error = null;
            })
            .addCase(getAllocationLogs.rejected, (state, action) => {
                state.loading.allocationLogs = false;
                state.error = action.payload as string;
            });
    },
});

export default assetManagementSlice.reducer;
